import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, ExternalLink, Download, Loader2, AlertCircle, Maximize2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Reports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeReportId, setActiveReportId] = useState(null);
  const [reportContent, setReportContent] = useState({});
  const [loadingContent, setLoadingContent] = useState({});
  const [errors, setErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const hasProcessedTicketId = useRef(false);

  const loadReportContent = useCallback(async (reportUrl, reportId) => {
    setLoadingContent(prev => {
      if (prev[reportId]) {
        // Already loading
        return prev;
      }
      return { ...prev, [reportId]: true };
    });
    setErrors(prev => ({ ...prev, [reportId]: null }));

    try {
      // Check if URL is a localhost URL that might not be accessible
      const isLocalhostUrl = reportUrl.includes('localhost') || reportUrl.includes('127.0.0.1');
      
      // Fetch the HTML content from the URL
      const response = await axios.get(reportUrl, {
        responseType: 'text',
        headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
        },
        timeout: 10000, // 10 second timeout
        validateStatus: (status) => status < 500 // Don't throw on 4xx errors
      });

      if (response.status === 200) {
        setReportContent(prev => {
          // Check if content already exists to avoid overwriting
          if (prev[reportId]) {
            return prev;
          }
          return {
            ...prev,
            [reportId]: response.data
          };
        });
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error(`Error loading report content for ${reportId}:`, error);
      
      let errorMessage = 'Failed to load report content. ';
      if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        errorMessage += 'The report server is not accessible. This may be a localhost URL that is not available. ';
      } else if (error.response?.status === 404) {
        errorMessage += 'Report file not found. The link may be expired or invalid. ';
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        errorMessage += 'Request timed out. The report server may be slow or unavailable. ';
      }
      errorMessage += 'You can try opening the report in a new tab using the "Open in New Tab" button.';
      
      setErrors(prev => ({
        ...prev,
        [reportId]: errorMessage
      }));
    } finally {
      setLoadingContent(prev => ({ ...prev, [reportId]: false }));
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, []);

  // Handle ticketId query parameter after reports are loaded
  useEffect(() => {
    const ticketIdParam = searchParams.get('ticketId');
    
    if (ticketIdParam && reports.length > 0 && !loading && !hasProcessedTicketId.current) {
      // Find the report with matching ticket ID
      const matchingReport = reports.find(r => String(r.ticket.id) === String(ticketIdParam));
      if (matchingReport) {
        hasProcessedTicketId.current = true;
        setActiveReportId(matchingReport.ticket.id);
        // Open the report in modal view
        setSelectedReport(matchingReport);
        setIsModalOpen(true);
        // Load content if not already loaded
        if (!reportContent[matchingReport.ticket.id]) {
          loadReportContent(matchingReport.reportUrl, matchingReport.ticket.id);
        }
        // Clear the query parameter from URL
        setSearchParams({});
      }
    } else if (reports.length > 0 && !activeReportId && !ticketIdParam && !loading) {
      // Set first report as active if no ticketId param and no active report
      setActiveReportId(reports[0].ticket.id);
    }
  }, [reports, loading, searchParams, reportContent, activeReportId, setSearchParams, loadReportContent]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const ticketsRes = await axios.get(`${API}/tickets`);
      
      // Filter tickets that are Static Report type
      const staticReportTickets = ticketsRes.data.filter(
        ticket => ticket.customization_type?.toLowerCase() === 'static report' ||
                  ticket.category?.toLowerCase() === 'static report'
      );

      // Fetch code generations for each static report ticket
      const reportsWithCode = await Promise.all(
        staticReportTickets.map(async (ticket) => {
          try {
            const codeRes = await axios.get(`${API}/code/${ticket.ticket_number}`);
            const activeCode = codeRes.data.find(c => c.is_active) || codeRes.data[0];
            
            if (activeCode && activeCode.code) {
              return {
                ticket,
                code: activeCode,
                reportUrl: activeCode.code
              };
            }
            return null;
          } catch (err) {
            console.error(`Error fetching code for ticket ${ticket.ticket_number}:`, err);
            return null;
          }
        })
      );

      const validReports = reportsWithCode.filter(r => r !== null);
      setReports(validReports);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReportSelect = (reportId, reportUrl) => {
    setActiveReportId(reportId);
    if (!reportContent[reportId]) {
      loadReportContent(reportUrl, reportId);
    }
  };

  const handleReportClick = (report) => {
    setSelectedReport(report);
    setIsModalOpen(true);
    // Load content if not already loaded
    if (!reportContent[report.ticket.id]) {
      loadReportContent(report.reportUrl, report.ticket.id);
    }
  };

  const downloadReport = (reportUrl, ticketNumber) => {
    const link = document.createElement('a');
    link.href = reportUrl;
    link.download = `report-${ticketNumber}.html`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground mt-2">View and manage static HTML reports</p>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No static reports available yet</p>
            <p className="text-sm text-muted-foreground">Static reports will appear here once generated for tickets</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const activeReport = reports.find(r => r.ticket.id === activeReportId) || reports[0];
  const currentContent = reportContent[activeReportId];
  const isLoading = loadingContent[activeReportId];
  const error = errors[activeReportId];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
        <p className="text-muted-foreground mt-2">View and manage static HTML reports</p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Reports List */}
        <div className="col-span-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Available Reports</CardTitle>
              <CardDescription>
                {reports.length} report{reports.length !== 1 ? 's' : ''} found
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {reports.map((report) => {
                  const isActive = report.ticket.id === activeReportId;
                  return (
                    <button
                      key={report.ticket.id}
                      onClick={() => handleReportClick(report)}
                      className={`
                        w-full text-left p-4 hover:bg-slate-50 transition-colors
                        ${isActive ? 'bg-blue-50 border-l-4 border-blue-600' : ''}
                      `}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="font-mono text-xs">
                              {report.ticket.ticket_number}
                            </Badge>
                            {isActive && (
                              <Badge className="bg-blue-600 text-white text-xs">Active</Badge>
                            )}
                          </div>
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {report.ticket.title}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(report.code.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Report Viewer */}
        <div className="col-span-9">
          <Card className="h-[calc(100vh-12rem)] flex flex-col">
            <CardHeader className="flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    {activeReport.ticket.title}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Ticket: {activeReport.ticket.ticket_number} • 
                    Generated: {new Date(activeReport.code.created_at).toLocaleString()}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleReportClick(activeReport)}
                  >
                    <Maximize2 className="h-4 w-4 mr-2" />
                    View Full Screen
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(activeReport.reportUrl, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open in New Tab
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadReport(activeReport.reportUrl, activeReport.ticket.ticket_number)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden p-0">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading report content...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="p-6">
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground mb-2">Report URL:</p>
                    <a
                      href={activeReport.reportUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 break-all text-sm"
                    >
                      {activeReport.reportUrl}
                    </a>
                  </div>
                </div>
              ) : currentContent ? (
                <iframe
                  srcDoc={currentContent}
                  className="w-full h-full border-0"
                  title={`Report ${activeReport.ticket.ticket_number}`}
                  sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-muted-foreground">Click on a report to view it</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Full-Width Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[95vh] max-h-[95vh] p-0 flex flex-col !left-[2.5vw] !top-[2.5vh] !translate-x-0 !translate-y-0">
          {selectedReport && (
            <>
              <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <DialogTitle className="flex items-center gap-2 text-xl">
                      <FileText className="h-5 w-5" />
                      {selectedReport.ticket.title}
                    </DialogTitle>
                    <DialogDescription className="mt-2">
                      Ticket: {selectedReport.ticket.ticket_number} • 
                      Generated: {new Date(selectedReport.code.created_at).toLocaleString()}
                    </DialogDescription>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(selectedReport.reportUrl, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open in New Tab
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadReport(selectedReport.reportUrl, selectedReport.ticket.ticket_number)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>
              </DialogHeader>
              <div className="flex-1 overflow-hidden p-0">
                {loadingContent[selectedReport.ticket.id] ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
                      <p className="text-muted-foreground">Loading report content...</p>
                    </div>
                  </div>
                ) : errors[selectedReport.ticket.id] ? (
                  <div className="p-6">
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{errors[selectedReport.ticket.id]}</AlertDescription>
                    </Alert>
                    <div className="mt-4">
                      <p className="text-sm text-muted-foreground mb-2">Report URL:</p>
                      <a
                        href={selectedReport.reportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 break-all text-sm"
                      >
                        {selectedReport.reportUrl}
                      </a>
                    </div>
                  </div>
                ) : reportContent[selectedReport.ticket.id] ? (
                  <iframe
                    srcDoc={reportContent[selectedReport.ticket.id]}
                    className="w-full h-full border-0"
                    title={`Report ${selectedReport.ticket.ticket_number}`}
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <p className="text-muted-foreground">Loading report...</p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
